import type { ApiError, AuthResponse } from './types'

const BASE = import.meta.env.VITE_API_BASE ?? ''

const ACCESS_KEY = 'sp.access'
const REFRESH_KEY = 'sp.refresh'

// How close to expiry (in seconds) we proactively refresh. The access token
// lives 1h; refreshing in the last 60s avoids the user-visible "request fails,
// then refresh, then retry" round-trip while still letting most requests reuse
// the existing token.
const PROACTIVE_REFRESH_THRESHOLD_SEC = 60

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY)
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY)
}

export function setTokens(access: string, refresh: string): void {
  localStorage.setItem(ACCESS_KEY, access)
  localStorage.setItem(REFRESH_KEY, refresh)
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY)
  localStorage.removeItem(REFRESH_KEY)
}

// Dispatched when the user's session is unrecoverably gone (no refresh token,
// or refresh was rejected by the backend). main.ts listens for this and routes
// the user to /login. Using a window event avoids importing the router/store
// from this low-level module and the circular dep that would create.
function notifyAuthExpired(): void {
  window.dispatchEvent(new CustomEvent('auth:expired'))
}

// Decode a JWT payload without verifying the signature. Used only to read
// the local `exp` claim for proactive refresh — never to trust user data.
function decodeJwtExp(token: string): number | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const data = JSON.parse(json) as { exp?: number }
    return typeof data.exp === 'number' ? data.exp : null
  } catch {
    return null
  }
}

function accessTokenExpiringSoon(): boolean {
  const token = getAccessToken()
  if (!token) return false
  const exp = decodeJwtExp(token)
  if (exp === null) return false
  return exp - Math.floor(Date.now() / 1000) < PROACTIVE_REFRESH_THRESHOLD_SEC
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | undefined | null>
  // Skip auto-attaching the bearer token. Used by /auth/login etc.
  noAuth?: boolean
  // Skip the 401-triggered refresh attempt. Used by /auth/refresh itself.
  noRefresh?: boolean
}

let refreshInFlight: Promise<boolean> | null = null

async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight

  const refresh = getRefreshToken()
  if (!refresh) return false

  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      })
      if (!res.ok) {
        clearTokens()
        notifyAuthExpired()
        return false
      }
      const data = (await res.json()) as AuthResponse
      setTokens(data.access_token, data.refresh_token)
      return true
    } catch {
      clearTokens()
      notifyAuthExpired()
      return false
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

function buildURL(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE}${path}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue
    params.set(k, String(v))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  // Proactively refresh if the access token is about to expire. This keeps the
  // typical request path on the fast branch (no 401 round-trip) once we're
  // close to expiry. If the refresh fails, refreshAccessToken() has already
  // dispatched auth:expired, and the request below will 401 — falling through
  // to the reactive branch which does nothing further.
  if (!opts.noAuth && !opts.noRefresh && accessTokenExpiringSoon()) {
    await refreshAccessToken()
  }

  const headers: Record<string, string> = {}
  let body: BodyInit | undefined

  if (opts.body !== undefined) {
    if (opts.body instanceof FormData) {
      body = opts.body
    } else {
      headers['Content-Type'] = 'application/json'
      body = JSON.stringify(opts.body)
    }
  }

  if (!opts.noAuth) {
    const token = getAccessToken()
    if (token) headers['Authorization'] = `Bearer ${token}`
  }

  const res = await fetch(buildURL(path, opts.query), {
    method: opts.method ?? 'GET',
    headers,
    body,
  })

  if (res.status === 401 && !opts.noRefresh && !opts.noAuth) {
    const refreshed = await refreshAccessToken()
    if (refreshed) {
      return apiRequest<T>(path, { ...opts, noRefresh: true })
    }
    // Refresh attempted and failed (or no refresh token existed).
    // refreshAccessToken already fired auth:expired in the first case; do it
    // here for the second so main.ts always routes to /login on hard 401.
    notifyAuthExpired()
  }

  if (!res.ok) {
    let message = `HTTP ${res.status}`
    try {
      const data = (await res.json()) as { error?: string }
      if (data?.error) message = data.error
    } catch {
      // body wasn't JSON; keep the generic message
    }
    const err: ApiError = { status: res.status, message }
    throw err
  }

  if (res.status === 204) {
    return undefined as T
  }
  const text = await res.text()
  return text ? (JSON.parse(text) as T) : (undefined as T)
}

// Fetch a binary resource with auth and return it as a Blob. Same token dance
// as apiRequest; used by the 3D preview, which needs an object URL rather
// than a download.
export async function apiFetchBlob(path: string): Promise<Blob> {
  if (accessTokenExpiringSoon()) {
    await refreshAccessToken()
  }

  const request = () => {
    const token = getAccessToken()
    return fetch(buildURL(path), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
  }

  let res = await request()
  if (res.status === 401) {
    const refreshed = await refreshAccessToken()
    if (!refreshed) {
      notifyAuthExpired()
      throw { status: 401, message: 'HTTP 401' } satisfies ApiError
    }
    res = await request()
  }
  if (!res.ok) {
    throw { status: res.status, message: `HTTP ${res.status}` } satisfies ApiError
  }
  return res.blob()
}

// Fetch a file with auth and hand it to the browser as a download. Mirrors
// apiRequest's token handling (proactive + reactive refresh) but keeps the
// body as a blob instead of parsing JSON. Filename comes from the response's
// Content-Disposition when present (same-origin, so the header is readable).
export async function apiDownload(
  path: string,
  query?: RequestOptions['query'],
  fallbackName = 'download',
): Promise<void> {
  if (accessTokenExpiringSoon()) {
    await refreshAccessToken()
  }

  const request = () => {
    const token = getAccessToken()
    return fetch(buildURL(path, query), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
  }

  let res = await request()
  if (res.status === 401) {
    const refreshed = await refreshAccessToken()
    if (!refreshed) {
      notifyAuthExpired()
      throw { status: 401, message: 'HTTP 401' } satisfies ApiError
    }
    res = await request()
  }
  if (!res.ok) {
    throw { status: res.status, message: `HTTP ${res.status}` } satisfies ApiError
  }

  const disposition = res.headers.get('Content-Disposition') ?? ''
  const match = /filename="([^"]+)"/.exec(disposition)
  const filename = match?.[1] ?? fallbackName

  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}
